import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchAuthenticatedRaw } from "../services/tokenHelper";
import { mediaFetchLimiter } from "../services/mediaFetchLimiter";

interface AuthenticatedVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  /**
   * Authenticated backend `/video` endpoint (e.g. `.../media/:id/video`).
   * If provided, we'll fetch a presigned URL from `${presignEndpoint}/presigned?type=${variant}`
   * and set it directly on `<video src>`, enabling native progressive buffering/seek.
   */
  presignEndpoint?: string;
  variant?: "original" | "playback";
  /**
   * Direct video URL (public/presigned). Used as-is. Prefer `presignEndpoint` for authenticated content.
   */
  src?: string;
  className?: string;
  onLoadedData?: () => void;
  onError?: () => void;
}

const NATIVE_CONTROLS_HIDE_MS = 4500;

function isPlaybackAtEnd(video: HTMLVideoElement): boolean {
  const { currentTime, duration } = video;
  if (!Number.isFinite(currentTime) || currentTime < 0) {
    return false;
  }
  if (Number.isFinite(duration) && duration > 0) {
    return currentTime >= Math.max(0, duration - 0.5);
  }
  return video.ended;
}

const AuthenticatedVideo: React.FC<AuthenticatedVideoProps> = ({
  src,
  presignEndpoint,
  variant = "playback",
  className = "",
  onLoadedData,
  onError,
  poster: posterSrc,
  onPlay,
  onPause,
  onEnded,
  ...videoProps
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsHideTimerRef = useRef<number | null>(null);
  const hasStartedPlaybackRef = useRef(false);
  const [videoSrc, setVideoSrc] = useState<string>("");
  const [isResolvingSrc, setIsResolvingSrc] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
  const [nativeControlsVisible, setNativeControlsVisible] = useState(false);
  const posterBlobUrlRef = useRef<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | undefined>(undefined);
  const posterRequestIdRef = useRef(0);
  const videoRequestIdRef = useRef(0);

  const clearControlsHideTimer = useCallback(() => {
    if (controlsHideTimerRef.current != null) {
      window.clearTimeout(controlsHideTimerRef.current);
      controlsHideTimerRef.current = null;
    }
  }, []);

  const markNativeControlsVisible = useCallback(() => {
    setNativeControlsVisible(true);
    clearControlsHideTimer();
    controlsHideTimerRef.current = window.setTimeout(() => {
      controlsHideTimerRef.current = null;
      setNativeControlsVisible(false);
    }, NATIVE_CONTROLS_HIDE_MS);
  }, [clearControlsHideTimer]);

  useEffect(() => {
    return () => clearControlsHideTimer();
  }, [clearControlsHideTimer]);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 25_000);
    const requestId = ++posterRequestIdRef.current;

    const clearPoster = () => {
      if (posterBlobUrlRef.current) {
        URL.revokeObjectURL(posterBlobUrlRef.current);
        posterBlobUrlRef.current = null;
      }
      setPosterUrl(undefined);
    };

    const fetchPoster = async () => {
      if (!posterSrc) {
        clearPoster();
        return;
      }

      // If caller already provided a blob/data URL, just use it.
      if (posterSrc.startsWith("blob:") || posterSrc.startsWith("data:")) {
        clearPoster();
        setPosterUrl(posterSrc);
        return;
      }

      let release: (() => void) | null = null;
      try {
        clearPoster();
        release = await mediaFetchLimiter.acquire({ signal: controller.signal });
        const response = await fetchAuthenticatedRaw(posterSrc, {
          signal: controller.signal,
          cache: "no-store",
        });

        // `fetchAuthenticatedRaw` already throws on non-2xx. This additionally ensures
        // we don't create an `<img>`/poster blob URL from a non-image response.
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.toLowerCase().startsWith("image/")) {
          throw new Error(`Unexpected content-type for poster: ${contentType || "(missing)"}`);
        }

        const blob = await response.blob();
        if (controller.signal.aborted || requestId !== posterRequestIdRef.current) return;

        if (posterBlobUrlRef.current) {
          URL.revokeObjectURL(posterBlobUrlRef.current);
        }

        const blobUrl = URL.createObjectURL(blob);
        posterBlobUrlRef.current = blobUrl;
        setPosterUrl(blobUrl);
      } catch (error) {
        if (!controller.signal.aborted && requestId === posterRequestIdRef.current) {
          // Non-fatal: if poster fails, video can still load/play.
          console.warn("Error loading authenticated video poster:", error);
          clearPoster();
          // Fallback: if this URL is public, let the browser try to load it natively.
          setPosterUrl(posterSrc);
        }
      } finally {
        window.clearTimeout(timeoutId);
        release?.();
      }
    };

    void fetchPoster();

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
      clearPoster();
    };
  }, [posterSrc]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++videoRequestIdRef.current;

    const resolveVideoSrc = async () => {
      try {
        setIsResolvingSrc(true);
        setHasError(false);
        setIsReady(false);
        setIsPlaying(false);
        setHasStartedPlayback(false);
        hasStartedPlaybackRef.current = false;
        setNativeControlsVisible(false);
        clearControlsHideTimer();
        // Avoid showing the previous video while the new one is loading.
        setVideoSrc("");

        if (presignEndpoint) {
          const presignedUrl = `${presignEndpoint}/presigned?type=${encodeURIComponent(variant)}`;
          const response = await fetchAuthenticatedRaw(presignedUrl, {
            signal: controller.signal,
            cache: "no-store",
          });

          const data = (await response.json()) as { url?: string };
          if (controller.signal.aborted || requestId !== videoRequestIdRef.current) return;

          if (!data?.url) {
            throw new Error("Presigned video URL response missing `url`");
          }

          setVideoSrc(data.url);
          return;
        }

        if (!src) {
          throw new Error("No video source provided");
        }

        // Direct URL (public/presigned). Let the browser stream it natively.
        setVideoSrc(src);
      } catch (error) {
        if (!controller.signal.aborted && requestId === videoRequestIdRef.current) {
          console.error("Error loading authenticated video:", error);
          setHasError(true);
          onError?.();
        }
      } finally {
        if (!controller.signal.aborted && requestId === videoRequestIdRef.current) {
          setIsResolvingSrc(false);
        }
      }
    };

    void resolveVideoSrc();

    return () => {
      controller.abort();
    };
  }, [clearControlsHideTimer, presignEndpoint, src, variant, onError]);

  const handleLoadedData = () => {
    setIsReady(true);
    onLoadedData?.();
  };

  const handleVideoError = () => {
    setHasError(true);
    onError?.();
  };

  const handlePlay = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    clearControlsHideTimer();
    setNativeControlsVisible(false);
    hasStartedPlaybackRef.current = true;
    setHasStartedPlayback(true);
    setIsPlaying(true);
    onPlay?.(event);
  };

  const handlePause = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    setIsPlaying(false);
    if (hasStartedPlaybackRef.current && !event.currentTarget.ended) {
      markNativeControlsVisible();
    }
    onPause?.(event);
  };

  const handleEnded = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    clearControlsHideTimer();
    setIsPlaying(false);
    hasStartedPlaybackRef.current = false;
    setHasStartedPlayback(false);
    setNativeControlsVisible(false);
    try {
      event.currentTarget.currentTime = 0;
    } catch {
      // ignore
    }
    onEnded?.(event);
  };

  const handlePlayClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    try {
      clearControlsHideTimer();
      setNativeControlsVisible(false);
      if (isPlaybackAtEnd(video)) {
        video.currentTime = 0;
      }
      await video.play();
    } catch (error) {
      console.warn("Failed to start video playback:", error);
    }
  };

  const showPoster = !hasStartedPlayback;
  const showLoadingSpinner = !hasError && !hasStartedPlayback && (isResolvingSrc || !isReady);
  // Before first play, keep the Play control up (poster covers native controls anyway).
  const showPlayOverlay = !hasError && isReady && !isPlaying && (hasStartedPlayback ? !nativeControlsVisible : true);

  return (
    <div data-section="authenticated-video-root" className="relative w-full h-full flex items-center justify-center">
      {!hasError ? (
        <video
          ref={videoRef}
          src={videoSrc || undefined}
          poster={posterUrl}
          className={className}
          preload="auto"
          onLoadedData={handleLoadedData}
          onCanPlay={handleLoadedData}
          onError={handleVideoError}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          {...videoProps}
          controls={hasStartedPlayback}
        />
      ) : (
        <div className={`${className} flex items-center justify-center bg-black/40`}>
          <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M9.17 9.17l9.9 9.9M12 5a7 7 0 110 14 7 7 0 010-14z"
            />
          </svg>
        </div>
      )}

      {showPoster && posterUrl && !hasError && (
        <div className="absolute inset-0 z-[1] bg-black" aria-hidden="true">
          <img src={posterUrl} alt="" className="h-full w-full object-contain" />
        </div>
      )}

      {showLoadingSpinner && (
        <div className="absolute inset-0 z-[2] flex items-center justify-center pointer-events-none">
          <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {showPlayOverlay && (
        <div className="absolute inset-0 z-[3] flex items-center justify-center pointer-events-none">
          <button
            type="button"
            className="pointer-events-auto flex h-[88px] w-[88px] cursor-pointer items-center justify-center rounded-full border-2 border-white/55 bg-black/55 text-white transition-colors hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Play video"
            onClick={handlePlayClick}
          >
            <svg className="ml-1 h-10 w-10" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthenticatedVideo;
